import { useRef, useState, useEffect } from "react";
import { toast } from "sonner";
import { Play, Pause, Heart, Trash, DownloadSimple, Plus, MusicNotes, ShareNetwork, Star, MagicWand } from "@phosphor-icons/react";
import { apiClient, audioSrc } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { useAuth } from "@/context/AuthContext";

function WaveformBars({ playing, count = 32 }) {
  const bars = Array.from({ length: count });
  return (
    <div className={`flex items-end gap-[2px] h-8 ${playing ? "wave-playing" : ""}`}>
      {bars.map((_, i) => {
        const h = 20 + ((i * 7) % 80);
        return (
          <span
            key={i}
            className="wave-bar w-[3px] bg-[color:var(--accent)] opacity-80 rounded-full"
            style={{ height: `${h}%`, animationDelay: `${(i * 60) % 800}ms` }}
          />
        );
      })}
    </div>
  );
}

function SoundCard({ sound, onToggleFavorite, onDelete, onDownload, onAddToMixer, categories, isAdmin, onRefined }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [featured, setFeatured] = useState(!!sound.featured);
  const [refineOpen, setRefineOpen] = useState(false);
  const [hint, setHint] = useState("");
  const [refining, setRefining] = useState(false);
  const { t } = useLang();

  const refine = async () => {
    if (hint.trim().length < 3) return;
    setRefining(true);
    try {
      const { data } = await apiClient.post(`/sounds/${sound.sound_id}/refine`, { hint: hint.trim() });
      onRefined?.(data);
      toast.success(t("refineDone"));
      setRefineOpen(false);
      setHint("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rework failed");
    } finally {
      setRefining(false);
    }
  };

  const toggleFeature = async () => {
    try {
      const { data } = await apiClient.patch(`/sounds/${sound.sound_id}/feature`);
      setFeatured(data.featured);
      toast.success(data.featured ? t("featOn") : t("featOff"));
    } catch {
      toast.error("Failed");
    }
  };

  const share = async () => {
    setSharing(true);
    try {
      const { data } = await apiClient.post(`/sounds/${sound.sound_id}/share`);
      const url = `${window.location.origin}/api/s/${data.share_id}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success(t("linkCopied"));
      } catch {
        toast.info(url, { duration: 10000 });
      }
    } catch {
      toast.error("Share failed");
    } finally {
      setSharing(false);
    }
  };

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onEnd = () => setPlaying(false);
    a.addEventListener("ended", onEnd);
    return () => a.removeEventListener("ended", onEnd);
  }, []);

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      setPlaying(false);
    } else {
      a.play();
      setPlaying(true);
    }
  };

  const cat = categories.find((c) => c.id === sound.category);

  return (
    <div
      data-testid="sound-card"
      className="group relative rounded-xl border border-white/10 bg-[color:var(--surface-2)] p-4 hover:border-white/25 transition-colors"
    >
      <audio ref={audioRef} src={audioSrc(sound)} preload="none" crossOrigin="use-credentials" />
      <div className="flex items-start gap-3">
        <button
          data-testid="play-sound-btn"
          onClick={togglePlay}
          className="shrink-0 w-11 h-11 rounded-full bg-[color:var(--accent)] hover:bg-[color:var(--accent-hover)] grid place-items-center transition-colors"
          aria-label={playing ? "Pause" : "Play"}
        >
          {playing ? <Pause size={18} weight="fill" color="white" /> : <Play size={18} weight="fill" color="white" />}
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-white/90 line-clamp-2 leading-snug">{sound.prompt}</p>
          <div className="flex items-center gap-2 mt-1.5 text-[10px] font-mono-num uppercase tracking-widest text-white/40">
            {cat && <span>{cat.name}</span>}
            {cat && <span>·</span>}
            <span>{sound.duration_seconds ? `${Number(sound.duration_seconds).toFixed(1)}s` : "auto"}</span>
            {sound.loop && <><span>·</span><span>loop</span></>}
            {sound.kind === "music" && (
              <span className="ml-1 px-1.5 py-0.5 rounded-sm bg-emerald-500/20 text-emerald-300 tracking-widest" data-testid="music-badge">MUSIC</span>
            )}
            {sound.kind === "speech" && (
              <span className="ml-1 px-1.5 py-0.5 rounded-sm bg-sky-500/20 text-sky-300 tracking-widest" data-testid="voice-badge">VOICE</span>
            )}
            {sound.is_mix && (
              <span className="ml-1 px-1.5 py-0.5 rounded-sm bg-[color:var(--accent)]/20 text-[color:var(--accent)] tracking-widest" data-testid="mix-badge">MIX</span>
            )}
            {sound.demo && (
              <span className="ml-1 px-1.5 py-0.5 rounded-sm bg-[color:var(--warning)]/20 text-[color:var(--warning)] tracking-widest">DEMO</span>
            )}
          </div>
          <div className="mt-2"><WaveformBars playing={playing} count={28} /></div>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-end gap-1">
        {!sound.is_mix && sound.kind !== "speech" && (
          <button
            data-testid="refine-sound-btn"
            onClick={() => setRefineOpen((o) => !o)}
            title={t("refineBtn")}
            className={`p-2 rounded-lg transition-colors ${refineOpen ? "bg-white/10 text-white" : "hover:bg-white/5 text-white/60 hover:text-white"}`}
          >
            <MagicWand size={16} />
          </button>
        )}
        <button
          data-testid="wav-download-btn"
          onClick={() => onDownload(sound, "wav")}
          title="WAV"
          className="px-1.5 py-1 rounded-md text-[9px] font-mono-num uppercase tracking-widest bg-white/5 text-white/50 hover:text-white transition-colors"
        >
          WAV
        </button>
        {isAdmin && (
          <button
            data-testid="feature-sound-btn"
            onClick={toggleFeature}
            title={t("featureBtn")}
            className="p-2 rounded-lg hover:bg-white/5 transition-colors"
          >
            <Star size={16} weight={featured ? "fill" : "regular"} className={featured ? "text-[color:var(--warning)]" : "text-white/60"} />
          </button>
        )}
        <button
          data-testid="add-to-mixer-btn"
          onClick={() => onAddToMixer(sound)}
          title="Add to mixer"
          className="p-2 rounded-lg hover:bg-white/5 transition-colors text-white/60 hover:text-white"
        >
          <Plus size={16} weight="bold" />
        </button>
        <button
          data-testid="favorite-btn"
          onClick={() => onToggleFavorite(sound.sound_id)}
          title="Favorite"
          className="p-2 rounded-lg hover:bg-white/5 transition-colors"
        >
          <Heart
            size={16}
            weight={sound.favorite ? "fill" : "regular"}
            className={sound.favorite ? "text-[color:var(--accent)]" : "text-white/60"}
          />
        </button>
        <button
          data-testid="share-sound-btn"
          onClick={share}
          disabled={sharing}
          title={t("share")}
          className="p-2 rounded-lg hover:bg-white/5 transition-colors text-white/60 hover:text-white disabled:opacity-40"
        >
          <ShareNetwork size={16} />
        </button>
        <button
          data-testid="download-btn"
          onClick={() => onDownload(sound)}
          title="Download"
          className="p-2 rounded-lg hover:bg-white/5 transition-colors text-white/60 hover:text-white"
        >
          <DownloadSimple size={16} />
        </button>
        <button
          data-testid="delete-btn"
          onClick={() => onDelete(sound.sound_id)}
          title="Delete"
          className="p-2 rounded-lg hover:bg-white/5 transition-colors text-white/60 hover:text-red-400"
        >
          <Trash size={16} />
        </button>
      </div>
      {refineOpen && (
        <div className="mt-2 flex gap-2" data-testid="refine-panel">
          <input
            data-testid="refine-hint-input"
            value={hint}
            onChange={(e) => setHint(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && refine()}
            maxLength={300}
            placeholder={t("refinePh")}
            className="flex-1 bg-[color:var(--surface)] border border-white/10 rounded-lg px-3 py-2 text-xs text-white placeholder:text-white/30 outline-none focus:border-white/25"
          />
          <button
            data-testid="refine-confirm-btn"
            onClick={refine}
            disabled={refining || hint.trim().length < 3}
            className="px-3 py-2 rounded-lg bg-[color:var(--accent)] hover:bg-[color:var(--accent-hover)] text-white text-xs font-medium disabled:opacity-40 transition-colors"
          >
            {refining ? "…" : `${t("refineBtn")} · ${sound.kind === "music" ? 3 : 1} ${t("creditsShort")}`}
          </button>
        </div>
      )}
    </div>
  );
}

export default function SoundLibrary({
  sounds, loading, activeCategory, setActiveCategory,
  favoritesOnly, setFavoritesOnly,
  onToggleFavorite, onDelete, onDownload, onAddToMixer, onRefined,
}) {
  const [categories, setCategories] = useState([]);
  const [mixesOnly, setMixesOnly] = useState(false);
  const { t } = useLang();
  const { user } = useAuth();

  useEffect(() => {
    apiClient.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
  }, []);

  return (
    <section
      data-testid="library-panel"
      className="rounded-2xl border border-white/10 bg-[color:var(--surface)] overflow-hidden h-full flex flex-col"
    >
      <div className="px-6 py-5 border-b border-white/5 flex items-center justify-between">
        <div>
          <p className="text-xs font-mono-num uppercase tracking-widest text-white/40">{t("libraryKicker")}</p>
          <h2 className="font-display font-semibold text-xl tracking-tight mt-0.5">{t("yourSounds")}</h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="mixes-toggle"
            onClick={() => setMixesOnly(!mixesOnly)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors font-mono-num uppercase tracking-widest ${
              mixesOnly
                ? "bg-[color:var(--accent)] border-[color:var(--accent)] text-white"
                : "border-white/10 text-white/60 hover:text-white hover:border-white/25"
            }`}
          >
            {t("mixes")}
          </button>
          <button
            data-testid="favorites-toggle"
            onClick={() => setFavoritesOnly(!favoritesOnly)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors font-mono-num uppercase tracking-widest ${
              favoritesOnly
                ? "bg-[color:var(--accent)] border-[color:var(--accent)] text-white"
                : "border-white/10 text-white/60 hover:text-white hover:border-white/25"
            }`}
          >
            ♥ {t("favorites")}
          </button>
        </div>
      </div>

      <div className="px-6 py-3 border-b border-white/5 flex gap-2 overflow-x-auto">
        <button
          data-testid="cat-filter-all"
          onClick={() => setActiveCategory(null)}
          className={`text-xs whitespace-nowrap px-3 py-1.5 rounded-full transition-colors ${
            !activeCategory ? "bg-white/10 text-white" : "text-white/50 hover:text-white"
          }`}
        >
          {t("all")}
        </button>
        {categories.map((c) => (
          <button
            key={c.id}
            data-testid={`cat-filter-${c.id}`}
            onClick={() => setActiveCategory(c.id)}
            className={`text-xs whitespace-nowrap px-3 py-1.5 rounded-full transition-colors ${
              activeCategory === c.id ? "bg-white/10 text-white" : "text-white/50 hover:text-white"
            }`}
          >
            {c.name}
          </button>
        ))}
      </div>

      <div className="flex-1 p-4 space-y-3 overflow-y-auto max-h-[800px]">
        {loading ? (
          <div className="text-center py-16 text-white/40 text-sm">{t("loadingLib")}</div>
        ) : (mixesOnly ? sounds.filter((s) => s.is_mix) : sounds).length === 0 ? (
          <div className="text-center py-16">
            <MusicNotes size={36} weight="duotone" className="mx-auto text-white/20" />
            <p className="mt-3 text-white/40 text-sm">{t("emptyLibrary")}</p>
          </div>
        ) : (
          (mixesOnly ? sounds.filter((s) => s.is_mix) : sounds).map((s) => (
            <SoundCard
              key={s.sound_id}
              sound={s}
              categories={categories}
              isAdmin={!!user?.is_admin}
              onRefined={onRefined}
              onToggleFavorite={onToggleFavorite}
              onDelete={onDelete}
              onDownload={onDownload}
              onAddToMixer={onAddToMixer}
            />
          ))
        )}
      </div>
    </section>
  );
}
